import { useCallback, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { RefreshCw } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { ActivityList } from '../components';
import { activityService } from '../services';
import { ActivitySeverity, type GetActivityRequest } from '../types';

const SEVERITY_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'All', value: 'all' },
  { label: 'Info', value: String(ActivitySeverity.Info) },
  { label: 'Notable', value: String(ActivitySeverity.Notable) },
  { label: 'Critical', value: String(ActivitySeverity.Critical) },
];

interface FilterState {
  module: string;
  actorUserId: string;
  entityType: string;
  entityId: string;
  actionType: string;
  fromUtc: string;
  toUtc: string;
  severity: string;
}

const EMPTY_FILTER: FilterState = {
  module: '',
  actorUserId: '',
  entityType: '',
  entityId: '',
  actionType: '',
  fromUtc: '',
  toUtc: '',
  severity: 'all',
};

export function ActivityPage() {
  usePageTitle('Activity');

  const [filterState, setFilterState] = useState<FilterState>(EMPTY_FILTER);
  // Bumping this forces the memoized `filter` to recompute on Refresh even when
  // the underlying inputs haven't changed.
  const [refreshKey, setRefreshKey] = useState(0);

  const filter = useMemo<GetActivityRequest>(() => {
    void refreshKey;
    const actorId = parseInt(filterState.actorUserId, 10);
    const sev = parseInt(filterState.severity, 10);
    return {
      module: filterState.module || undefined,
      actorUserId: Number.isFinite(actorId) ? actorId : undefined,
      entityType: filterState.entityType || undefined,
      entityId: filterState.entityId || undefined,
      actionType: filterState.actionType || undefined,
      fromUtc: filterState.fromUtc
        ? new Date(filterState.fromUtc).toISOString()
        : undefined,
      toUtc: filterState.toUtc
        ? new Date(filterState.toUtc).toISOString()
        : undefined,
      severity: Number.isFinite(sev) ? (sev as ActivitySeverity) : undefined,
      pageSize: 50,
    };
  }, [filterState, refreshKey]);

  const update = <K extends keyof FilterState>(key: K, value: FilterState[K]) =>
    setFilterState((prev) => ({ ...prev, [key]: value }));

  // Admin feed fetcher — hits /api/activity with the user's filter state.
  //
  // ⚠️ Memoized on `filter` identity, and that identity is LOAD-BEARING: it is what resets the list back to
  // the first page when a filter changes, because `ActivityList` keys its reset effect on the fetcher. The
  // Refresh button works the same way — `refreshKey` is read inside the `filter` memo purely to change its
  // identity. That `void refreshKey` looks like dead code and is not; removing it breaks Refresh with no
  // type error and no test failure.
  const fetcher = useCallback(
    (cursor: { occurredAtUtc: string; id: number } | null) =>
      activityService.getPaged({
        ...filter,
        ...(cursor ? { cursorOccurredAtUtc: cursor.occurredAtUtc, cursorId: cursor.id } : {}),
      }),
    [filter],
  );

  return (
    <div className="container mx-auto space-y-6">
      <PageHeader
        title="Activity"
        actions={
          <Button variant="outline" size="sm" onClick={() => setRefreshKey((k) => k + 1)}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label htmlFor="filter-module">Module</Label>
              <Input
                id="filter-module"
                placeholder="e.g. Picklist"
                value={filterState.module}
                onChange={(e) => update('module', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="filter-actor">Actor User ID</Label>
              <Input
                id="filter-actor"
                type="number"
                placeholder="e.g. 12"
                value={filterState.actorUserId}
                onChange={(e) => update('actorUserId', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="filter-entity-type">Entity Type</Label>
              <Input
                id="filter-entity-type"
                placeholder="e.g. Picklist"
                value={filterState.entityType}
                onChange={(e) => update('entityType', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="filter-entity-id">Entity ID</Label>
              <Input
                id="filter-entity-id"
                placeholder="e.g. 88"
                value={filterState.entityId}
                onChange={(e) => update('entityId', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="filter-action">Action Type</Label>
              <Input
                id="filter-action"
                placeholder="e.g. PicklistPosted"
                value={filterState.actionType}
                onChange={(e) => update('actionType', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="filter-from">From</Label>
              <Input
                id="filter-from"
                type="datetime-local"
                value={filterState.fromUtc}
                onChange={(e) => update('fromUtc', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="filter-to">To</Label>
              <Input
                id="filter-to"
                type="datetime-local"
                value={filterState.toUtc}
                onChange={(e) => update('toUtc', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="filter-severity">Severity</Label>
              <Select
                value={filterState.severity}
                onValueChange={(v) => update('severity', v)}
              >
                <SelectTrigger id="filter-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilterState(EMPTY_FILTER)}
            >
              Clear filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <ActivityList fetcher={fetcher} showEntity />
    </div>
  );
}
